import { useEffect } from 'react';
import { useAuthStore } from '../store/authStore';

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const checkAuth = useAuthStore((state) => state.checkAuth);
  const login = useAuthStore((state) => state.login);
  const user = useAuthStore((state) => state.user);

  useEffect(() => {
    // Restaura flag do embed do cockpit (iframes do /simulador)
    try {
      if (sessionStorage.getItem('ac_cockpit_embed') === '1') {
        document.documentElement.classList.add('ac-cockpit-embed');
      }
    } catch { /* noop */ }

    // DEMO MODE: Detectar demo_token no URL para auto-login
    const urlParams = new URLSearchParams(window.location.search);
    const demoToken = urlParams.get('demo_token');
    if (demoToken) {
      localStorage.setItem('ac_token', demoToken);
      localStorage.setItem('ac_demo_mode', 'true');
      // Remover param do URL para evitar loops
      urlParams.delete('demo_token');
      const cleanUrl = urlParams.toString()
        ? `${window.location.pathname}?${urlParams.toString()}`
        : window.location.pathname;
      window.history.replaceState({}, '', cleanUrl);
    }

    // IMPERSONATION: Se a URL contém ?impersonate=, preparar sessionStorage
    // ANTES de chamar checkAuth para que o token correto seja usado.
    const impData = urlParams.get('impersonate');
    if (impData) {
      try {
        const data = JSON.parse(decodeURIComponent(impData));
        if (data.access_token) {
          sessionStorage.setItem('ac_impersonate', 'true');
          sessionStorage.setItem('ac_token', data.access_token);
          if (data.skip_splash || data.from_cockpit) {
            sessionStorage.setItem('ac_skip_splash', '1');
            sessionStorage.setItem('ac_cockpit_embed', '1');
            try {
              document.documentElement.classList.add('ac-cockpit-embed');
            } catch { /* noop */ }
          }
          if (data.user) {
            sessionStorage.setItem('ac_user', JSON.stringify(data.user));
            if (data.user.impersonatedByName) {
              sessionStorage.setItem('ac_impersonatedByName', data.user.impersonatedByName);
            }
            if (data.user.perfil) sessionStorage.setItem('ac_perfil', data.user.perfil);
            if (data.user.cargo) sessionStorage.setItem('ac_cargo', data.user.cargo);
            if (data.user.escolaId) sessionStorage.setItem('ac_escolaId', data.user.escolaId);
            if (data.user.nomeEscola) sessionStorage.setItem('ac_nomeEscola', data.user.nomeEscola);
            if (data.user.simulationSessionId || data.user.isSimulation) {
              sessionStorage.setItem(
                'ac_sim_session',
                String(data.user.simulationSessionId || '1'),
              );
            }
          }
          if (data.redirect_url) {
            sessionStorage.setItem('ac_redirect_after_login', data.redirect_url);
          }

          // Cockpit/RESP: forçar tela de ativar notificações (URL some no redirect)
          const wantsPush =
            data.promptNotification === true ||
            data.promptNotification === 1 ||
            urlParams.get('promptNotification') === '1' ||
            String(data.user?.perfil || '').toUpperCase() === 'RESPONSAVEL';
          if (wantsPush) {
            sessionStorage.setItem('ac_force_push', 'true');
            try { localStorage.setItem('ac_force_push', 'true'); } catch { /* noop */ }
          }

          urlParams.delete('impersonate');
          const cleanUrl = urlParams.toString()
            ? `${window.location.pathname}?${urlParams.toString()}`
            : window.location.pathname;
          window.history.replaceState({}, '', cleanUrl);

          // Autentica na hora (mesmo se /auth/me falhar depois)
          if (data.user) {
            login(data.access_token, data.user as any);
          }
        }
      } catch { /* ignore parse errors */ }
    }

    if (urlParams.get('impersonate_receiver') === 'true') {
      // Bypassa o checkAuth inicial porque o token virá via postMessage
      return;
    }

    checkAuth();
  }, [checkAuth, login]);

  // Se for o receptor do iframe de impersonação, notifica o pai (com retries curtos)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('impersonate_receiver') !== 'true') return;
    if (window.parent === window) return;

    let attempts = 0;
    const ping = () => {
      if (sessionStorage.getItem('ac_impersonate') === 'true') return;
      window.parent.postMessage({ type: 'iframe_ready' }, window.location.origin);
      attempts += 1;
      if (attempts < 6) {
        timer = window.setTimeout(ping, 200);
      }
    };
    let timer = window.setTimeout(ping, 50);
    return () => clearTimeout(timer);
  }, []);

  // Listener para impersonação via postMessage segura (Torre de Controle de Simulação)
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'impersonate_login') {
        const {
          access_token,
          user: userData,
          redirect_url,
          skip_splash,
          from_cockpit,
          promptNotification,
        } = event.data.data || {};
        if (access_token && userData) {
          sessionStorage.setItem('ac_impersonate', 'true');
          sessionStorage.setItem('ac_token', access_token);
          sessionStorage.setItem('ac_user', JSON.stringify(userData));
          if (skip_splash || from_cockpit) {
            sessionStorage.setItem('ac_skip_splash', '1');
            sessionStorage.setItem('ac_cockpit_embed', '1');
            try {
              document.documentElement.classList.add('ac-cockpit-embed');
            } catch { /* noop */ }
          }
          if (userData.perfil) sessionStorage.setItem('ac_perfil', userData.perfil);
          if (userData.cargo) sessionStorage.setItem('ac_cargo', userData.cargo);
          if (userData.escolaId) sessionStorage.setItem('ac_escolaId', userData.escolaId);
          if (userData.nomeEscola) sessionStorage.setItem('ac_nomeEscola', userData.nomeEscola);
          if (userData.simulationSessionId || userData.isSimulation) {
            sessionStorage.setItem(
              'ac_sim_session',
              String(userData.simulationSessionId || '1'),
            );
          }

          if (redirect_url) {
            sessionStorage.setItem('ac_redirect_after_login', redirect_url);
          }

          const wantsPush =
            promptNotification === true ||
            promptNotification === 1 ||
            String(userData.perfil || '').toUpperCase() === 'RESPONSAVEL';
          if (wantsPush) {
            sessionStorage.setItem('ac_force_push', 'true');
            try { localStorage.setItem('ac_force_push', 'true'); } catch { /* noop */ }
          }

          // Autentica imediatamente com o payload (multi-escola / simulação)
          // e só depois tenta validar no backend — sem limpar se /auth/me falhar.
          login(access_token, userData as any);
          void checkAuth();
        }
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [checkAuth, login]);

  // Atualizar document.title dinamicamente com base no perfil e nome do usuário logado/impersonado
  useEffect(() => {
    if (user) {
      const prefixMap: Record<string, string> = {
        MASTER: 'ADMIN',
        ADMIN: 'ADMIN',
        MUNICIPAL: 'MUNIC',
        ESTADUAL: 'ESTAD',
        DIRETOR: 'DIRET',
        PROFESSOR: 'PROF',
        RESPONSAVEL: 'RESP',
        ALUNO: 'ALUNO',
      };
      const prefix = prefixMap[user.perfil] || user.perfil || 'USER';
      const shortName = user.nome ? user.nome.split(' ').slice(0, 2).join(' ') : '';
      document.title = `${prefix}: ${shortName || user.email || ''} | Aluno Conectado`;
    } else {
      document.title = 'Aluno Conectado';
    }
  }, [user]);

  /** Spinner global movido para AuthLoadingGate dentro do Router — rotas como /ativar não ficam bloqueadas. */
  return <>{children}</>;
}
